import { useMemo, useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { IconoActualizar } from '../componentes/iconos'
import {
  useAuditoria,
  type Ejecucion,
  type ProcesoEjecucion,
  type EstadoEjecucion,
} from '../hooks/useAuditoria'

/**
 * Panel de Auditoría: qué ha ejecutado el sistema y con qué resultado.
 *
 * Sustituye la exposición de los workflows crudos de n8n: aquí se ve, en
 * lenguaje claro, la actividad importante del ciclo (consulta del lunes,
 * recordatorio del miércoles, corte del viernes, recálculo, avisos), con su
 * resultado. Los chips de proceso filtran el histórico.
 */

const PROCESOS: { clave: ProcesoEjecucion; etiqueta: string }[] = [
  { clave: 'consulta_lunes', etiqueta: 'Consulta lunes' },
  { clave: 'recordatorio_miercoles', etiqueta: 'Recordatorio miércoles' },
  { clave: 'corte_viernes', etiqueta: 'Corte viernes' },
  { clave: 'recalculo', etiqueta: 'Recálculo' },
  { clave: 'aviso', etiqueta: 'Avisos' },
]

const NOMBRE_PROCESO: Record<ProcesoEjecucion, string> = {
  consulta_lunes: 'Consulta del lunes',
  recordatorio_miercoles: 'Recordatorio del miércoles',
  corte_viernes: 'Corte del viernes',
  recalculo: 'Recálculo de etiquetas',
  aviso: 'Envío de avisos',
}

/** Punto de color + etiqueta según el estado de la ejecución. */
function ChipEstado({ estado, simulado }: { estado: EstadoEjecucion; simulado: boolean }) {
  const map: Record<EstadoEjecucion, { txt: string; cls: string }> = {
    ok: { txt: 'OK', cls: 'text-positivo-tinta bg-positivo-fondo border-positivo-anillo' },
    simulado: { txt: 'Simulado', cls: 'text-acento-tinta bg-acento-suave border-acento-borde' },
    abortado: { txt: 'Abortado', cls: 'text-atencion-tinta bg-atencion-fondo border-atencion-anillo' },
    error: { txt: 'Error', cls: 'text-critico-tinta bg-critico-fondo border-critico-anillo' },
  }
  // 'simulado' como estado gana; si es ok pero simulado, lo marcamos igual.
  const clave: EstadoEjecucion = estado === 'ok' && simulado ? 'simulado' : estado
  const { txt, cls } = map[clave]
  return <span className={['shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold', cls].join(' ')}>{txt}</span>
}

/** Fecha ISO → "vie 3 oct, 16:00". */
function cuando(iso: string): string {
  const f = new Date(iso)
  return f.toLocaleString('es-ES', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function FilaEjecucion({ ejecucion }: { ejecucion: Ejecucion }) {
  return (
    <li className="flex flex-col gap-2 rounded-xl border border-borde bg-superficie px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-tinta">{NOMBRE_PROCESO[ejecucion.proceso]}</span>
          <ChipEstado estado={ejecucion.estado} simulado={ejecucion.simulado} />
        </div>
        <p className="text-sm text-tinta-2">{ejecucion.resumen}</p>
      </div>
      <span className="shrink-0 text-xs capitalize text-tinta-apagada tabular-nums">{cuando(ejecucion.iniciadaEn)}</span>
    </li>
  )
}

export function Auditoria() {
  const [filtro, setFiltro] = useState<ProcesoEjecucion | null>(null)
  const auditoria = useAuditoria(filtro ?? undefined)
  const items = useMemo(() => auditoria.data?.items ?? [], [auditoria.data])

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-tinta">Auditoría</h1>
          <p className="mt-1 text-sm text-tinta-apagada">
            Qué ha ejecutado el sistema y con qué resultado: consulta del lunes, recordatorio,
            corte del viernes, recálculo y avisos. Lo más reciente primero.
          </p>
        </div>
        <button
          type="button"
          aria-label="Actualizar"
          onClick={() => void auditoria.refetch()}
          className="flex items-center gap-2 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
        >
          <IconoActualizar className="size-4" />
          <span className="hidden sm:inline">Actualizar</span>
        </button>
      </div>

      {/* Chips de proceso para filtrar el histórico. */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setFiltro(null)}
          aria-pressed={filtro === null}
          className={[
            'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
            filtro === null ? 'bg-acento text-tinta-inversa' : 'border border-borde-fuerte text-tinta-2 hover:bg-elevado',
          ].join(' ')}
        >
          Todos
        </button>
        {PROCESOS.map((p) => (
          <button
            key={p.clave}
            type="button"
            onClick={() => setFiltro(filtro === p.clave ? null : p.clave)}
            aria-pressed={filtro === p.clave}
            className={[
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              filtro === p.clave ? 'bg-acento text-tinta-inversa' : 'border border-borde-fuerte text-tinta-2 hover:bg-elevado',
            ].join(' ')}
          >
            {p.etiqueta}
          </button>
        ))}
      </div>

      {auditoria.isPending ? (
        <SkeletonList />
      ) : auditoria.isError ? (
        <EstadoError error={auditoria.error} onReintentar={() => void auditoria.refetch()} />
      ) : items.length === 0 ? (
        <EstadoVacio
          titulo="Sin ejecuciones registradas"
          descripcion={
            filtro
              ? 'Este proceso aún no se ha ejecutado, o no en el histórico reciente.'
              : 'Cuando el sistema ejecute la consulta, el corte o el recálculo, aparecerán aquí.'
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((e) => (
            <FilaEjecucion key={e.id} ejecucion={e} />
          ))}
        </ul>
      )}
    </div>
  )
}
